import React from 'react';
import { 
  User, 
  Calendar, 
  Phone, 
  Mail, 
  Car, 
  Truck,
  Shield, 
  MapPin, 
  Clock,
  DollarSign,
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import LogiConnectIdBadge from '@/components/ui/logiconnect-id-badge';

const DriverDetailsDialog = ({ 
  driver, 
  isOpen, 
  onClose, 
  onEdit, 
  onDelete,
  onAssignVehicle,
  onUnassignVehicle,
  vehicles = [],
  isLoading = false 
}) => {
  if (!driver) return null;

  const getStatusBadge = (status) => {
    const statusConfig = {
      ACTIVE: { variant: 'default', icon: CheckCircle, label: 'Actif' },
      INACTIVE: { variant: 'secondary', icon: XCircle, label: 'Inactif' },
      SUSPENDED: { variant: 'destructive', icon: AlertCircle, label: 'Suspendu' },
    };
    
    const config = statusConfig[status] || statusConfig.INACTIVE;
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getLicenseStatus = (validUntil) => {
    if (!validUntil) return { status: 'unknown', label: 'Inconnu', variant: 'secondary' };
    
    const now = new Date();
    const validDate = new Date(validUntil);
    const daysUntilExpiry = Math.ceil((validDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return { status: 'expired', label: 'Expiré', variant: 'destructive' };
    } else if (daysUntilExpiry <= 30) {
      return { status: 'expiring_soon', label: 'Expire bientôt', variant: 'destructive' };
    } else {
      return { status: 'valid', label: 'Valide', variant: 'default' };
    }
  };

  const licenseStatus = getLicenseStatus(driver.license_valid_until);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {driver.first_name} {driver.last_name}
            {driver.logiconnect_id && (
              <LogiConnectIdBadge 
                id={driver.logiconnect_id} 
                showLabel={false}
                size="sm"
              />
            )}
          </DialogTitle>
          <DialogDescription>
            Détails complets du chauffeur
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* En-tête avec statut */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-wrap">
              {getStatusBadge(driver.status)}
              <Badge variant={licenseStatus.variant}>
                <Shield className="h-3 w-3 mr-1" />
                Permis {licenseStatus.label}
              </Badge>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => onEdit(driver)}>
                Modifier
              </Button>
              <Button variant="destructive" size="sm" onClick={() => onDelete(driver)}>
                Supprimer
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Informations personnelles */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Informations personnelles
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {driver.logiconnect_id && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">ID LogiConnect</label>
                    <div className="mt-2">
                      <LogiConnectIdBadge 
                        id={driver.logiconnect_id} 
                        showLabel={false}
                        size="default"
                      />
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Prénom</label>
                    <p className="text-sm">{driver.first_name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Nom</label>
                    <p className="text-sm">{driver.last_name}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span className="text-sm">{driver.phone}</span>
                  </div>
                  
                  {driver.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-gray-400" />
                      <span className="text-sm">{driver.email}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Informations professionnelles */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Informations professionnelles
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Numéro de permis</label>
                  <p className="text-sm font-mono">{driver.license_number}</p>
                </div>

                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <div>
                    <label className="text-sm font-medium text-gray-500">Permis valide jusqu'au</label>
                    <p className="text-sm">
                      {driver.license_valid_until 
                        ? format(new Date(driver.license_valid_until), 'PPP', { locale: fr })
                        : 'Non spécifié'
                      }
                    </p>
                  </div>
                </div>

                {driver.hire_date && (
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <div>
                      <label className="text-sm font-medium text-gray-500">Date d'embauche</label>
                      <p className="text-sm">
                        {format(new Date(driver.hire_date), 'PPP', { locale: fr })}
                      </p>
                    </div>
                  </div>
                )}

                {driver.salary && (
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-gray-400" />
                    <div>
                      <label className="text-sm font-medium text-gray-500">Salaire</label>
                      <p className="text-sm">{Number(driver.salary).toLocaleString('fr-FR')} FCFA</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Véhicule assigné */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Car className="h-4 w-4" />
                  Véhicule assigné
                </CardTitle>
              </CardHeader>
              <CardContent>
                {driver.assigned_vehicle ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Truck className="h-4 w-4 text-gray-400" />
                      <div>
                        <p className="font-medium">{driver.assigned_vehicle.plate_number}</p>
                        <p className="text-sm text-gray-500">
                          {driver.assigned_vehicle.make} {driver.assigned_vehicle.model}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => onUnassignVehicle(driver)}
                        disabled={isLoading}
                      >
                        Désassigner
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-500">Aucun véhicule assigné</p>
                    {vehicles.length > 0 && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => onAssignVehicle(driver)}
                        disabled={isLoading}
                      >
                        Assigner un véhicule
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Notes */}
            {driver.notes && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Notes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {driver.notes}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Métadonnées */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Métadonnées</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-gray-500">
                <div>
                  <label className="font-medium">ID:</label>
                  <p className="font-mono">{driver.id}</p>
                </div>
                <div>
                  <label className="font-medium">Transporteur:</label>
                  <p className="font-mono">{driver.transporter}</p>
                </div>
                {driver.created_at && (
                  <div>
                    <label className="font-medium">Créé le:</label>
                    <p>{format(new Date(driver.created_at), 'PPP à HH:mm', { locale: fr })}</p>
                  </div>
                )}
                {driver.updated_at && (
                  <div>
                    <label className="font-medium">Modifié le:</label>
                    <p>{format(new Date(driver.updated_at), 'PPP à HH:mm', { locale: fr })}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DriverDetailsDialog;